import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FiCopy,
  FiZap,
  FiEye,
  FiRefreshCw,
  FiAlertTriangle,
  FiChevronLeft,
  FiChevronRight
} from 'react-icons/fi';

import { useRecipes } from '../../../hooks/useRecipes';
import MacSubscribePrompt from '../../amps/MacSubscribePrompt';
import RecipeDetailModal from './RecipeDetailModal';
import type { Recipe } from '../../../services/recipeService';

const STATUS_COLORS: Record<string, string> = {
  active: '#10b981',
  draft: '#6b7280',
  stopped: '#6b7280',
  processing: '#f59e0b',
  failed: '#ef4444'
};
const statusColor = (s: string) => STATUS_COLORS[s] ?? '#6b7280';

type Tab = 'mine' | 'global';

const TABS: { key: Tab; label: string }[] = [
  { key: 'mine', label: 'My Recipes' },
  { key: 'global', label: 'Global Recipes' }
];

const RecipesWidget: React.FC = () => {
  const [tab, setTab] = useState<Tab>('mine');
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [isSelectedMine, setIsSelectedMine] = useState(true);

  const {
    recipes,
    meta,
    isLoading,
    error,
    fetchRecipes,
    myRecipes,
    myMeta,
    isLoadingMy,
    myError,
    fetchMyRecipes,
    notSubscribed,
    isCloningKey,
    cloneSuccess,
    cloneError,
    cloneRecipe,
    isViewingInMac,
    viewInMacError,
    viewInMac,
    goToPage,
    goToMyPage
  } = useRecipes({ perPage: 5 });

  const isMine = tab === 'mine';
  const items = isMine ? myRecipes : recipes;
  const loading = isMine ? isLoadingMy : isLoading;
  const err = isMine ? myError : error;
  const retry = isMine ? fetchMyRecipes : fetchRecipes;
  const page = isMine ? (myMeta?.current_page ?? 1) : (meta?.current_page ?? 1);
  const totalPages = isMine
    ? (myMeta?.total_pages ?? 1)
    : (meta?.total_pages ?? 1);
  const onPage = isMine ? goToMyPage : goToPage;

  const openMineModal = (r: Recipe) => {
    setSelectedRecipe(r);
    setIsSelectedMine(true);
  };

  const openGlobalModal = (r: Recipe) => {
    setSelectedRecipe(r);
    setIsSelectedMine(false);
  };

  const actionSlot = selectedRecipe ? (
    isSelectedMine ? (
      <motion.button
        onClick={() => viewInMac(selectedRecipe.key)}
        disabled={isViewingInMac === selectedRecipe.key}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
        className={`mws-btn ${viewInMacError === selectedRecipe.key ? 'mws-btn--view-error' : 'mws-btn--view'}`}
      >
        <FiZap size={11} />
        {isViewingInMac === selectedRecipe.key
          ? 'Opening…'
          : viewInMacError === selectedRecipe.key
            ? 'Failed'
            : 'View in MAC'}
      </motion.button>
    ) : (
      <motion.button
        onClick={() => cloneRecipe(selectedRecipe.key)}
        disabled={
          isCloningKey === selectedRecipe.key ||
          cloneSuccess === selectedRecipe.key
        }
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
        className={`mws-btn ${
          cloneSuccess === selectedRecipe.key
            ? 'mws-btn--clone-success'
            : cloneError === selectedRecipe.key
              ? 'mws-btn--clone-error'
              : 'mws-btn--clone'
        }`}
      >
        <FiCopy size={11} />
        {isCloningKey === selectedRecipe.key
          ? 'Cloning…'
          : cloneSuccess === selectedRecipe.key
            ? 'Cloned!'
            : cloneError === selectedRecipe.key
              ? 'Failed'
              : 'Clone'}
      </motion.button>
    )
  ) : null;

  return (
    <>
      <div className='dw-card' style={{ padding: '12px 14px' }}>
        <div className='dw-card__label' style={{ marginBottom: 8 }}>
          Recipes
        </div>

        <div className='mws-tabs'>
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`mws-tab${tab === key ? ' mws-tab--active' : ''}`}
            >
              {label}
            </button>
          ))}
        </div>

        {isMine && notSubscribed ? (
          <MacSubscribePrompt />
        ) : loading ? (
          <div className='mws-skeletons'>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className='dw-skel mws-skel-row' />
            ))}
          </div>
        ) : err ? (
          <div className='mws-error'>
            <span className='mws-error__msg'>
              <FiAlertTriangle size={12} /> {err}
            </span>
            <button onClick={retry} className='mws-error__retry'>
              <FiRefreshCw size={10} /> Retry
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className='mws-empty'>No recipes found</div>
        ) : (
          <div className='mws-list'>
            {isMine
              ? items.map(recipe => {
                  const steps = recipe.recipe_apps?.length ?? 0;
                  const thisViewErr = viewInMacError === recipe.key;

                  return (
                    <div key={recipe.id} className='mws-item'>
                      <span
                        className='mws-item__dot'
                        style={{ background: statusColor(recipe.status) }}
                      />
                      <div className='mws-item__info'>
                        <div className='mws-item__info-name'>{recipe.name}</div>
                        <div className='mws-item__info-sub'>
                          {steps} step{steps !== 1 ? 's' : ''}
                          {recipe.folder_name && ` · ${recipe.folder_name}`}
                        </div>
                      </div>
                      <motion.button
                        onClick={() => openMineModal(recipe)}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                        className={`mws-btn ${thisViewErr ? 'mws-btn--view-error' : 'mws-btn--view'}`}
                      >
                        <FiEye size={9} />
                        {thisViewErr ? 'Error' : 'View'}
                      </motion.button>
                    </div>
                  );
                })
              : items.map(recipe => {
                  const steps = recipe.recipe_apps?.length ?? 0;
                  const thisCloning = isCloningKey === recipe.key;
                  const thisSuccess = cloneSuccess === recipe.key;
                  const thisError = cloneError === recipe.key;
                  const cloneClass = `mws-btn ${thisSuccess ? 'mws-btn--clone-success' : thisError ? 'mws-btn--clone-error' : 'mws-btn--clone'}`;

                  return (
                    <div key={recipe.id} className='mws-item'>
                      <span
                        className='mws-item__dot'
                        style={{ background: statusColor(recipe.status) }}
                      />
                      <div className='mws-item__info'>
                        <div className='mws-item__info-name'>{recipe.name}</div>
                        <div className='mws-item__info-sub'>
                          {steps} step{steps !== 1 ? 's' : ''}
                          {recipe.folder_name && ` · ${recipe.folder_name}`}
                        </div>
                      </div>
                      <motion.button
                        onClick={() => openGlobalModal(recipe)}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                        className='mws-btn mws-btn--view'
                      >
                        <FiEye size={9} />
                        View
                      </motion.button>
                      <motion.button
                        onClick={() => cloneRecipe(recipe.key)}
                        disabled={thisCloning || thisSuccess}
                        whileHover={thisCloning ? {} : { scale: 1.04 }}
                        whileTap={thisCloning ? {} : { scale: 0.96 }}
                        className={cloneClass}
                      >
                        <FiCopy size={9} />
                        {thisCloning
                          ? 'Cloning…'
                          : thisSuccess
                            ? 'Cloned!'
                            : thisError
                              ? 'Failed'
                              : 'Clone'}
                      </motion.button>
                    </div>
                  );
                })}
          </div>
        )}

        {!loading && !err && totalPages > 1 && (
          <div className='mws-pagination'>
            <button
              className='mws-pagination__btn'
              onClick={() => onPage(page - 1)}
              disabled={page === 1}
            >
              <FiChevronLeft size={12} />
            </button>
            <span className='mws-pagination__count'>
              {page} / {totalPages}
            </span>
            <button
              className='mws-pagination__btn'
              onClick={() => onPage(page + 1)}
              disabled={page === totalPages}
            >
              <FiChevronRight size={12} />
            </button>
          </div>
        )}
      </div>

      <RecipeDetailModal
        recipe={selectedRecipe}
        isMine={isSelectedMine}
        onClose={() => setSelectedRecipe(null)}
        actionSlot={actionSlot}
      />
    </>
  );
};

export default RecipesWidget;
